import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CertificateApiError,
  type CertificateHistoryItem,
  type CertificateMetadata,
  type CertificateStatusResponse,
  type UserMe,
  deleteCertificate,
  fetchMe,
  getActiveOrgId,
  getCertificate,
  getCertificateHistory,
  getCertificateStatus,
  rotateCertificate,
  uploadCertificate,
} from '../services/api';
import type { NavView } from '../components/Layout';
import { useLocale, useT, type TFunction } from '../i18n';
import { formatDate, formatDateTime } from '../i18n/format';
// Issue #550: Page shell — remove wrapper proprio (max-w-3xl p-6
// duplicava o <main> do Layout) e padroniza o header da pagina.
import Page from '../components/Page';

// Sprint 14 (S14-T07): Gestao de certificados digitais A1 — substitui o
// placeholder do issue #475. Custodia BYOC + sessao efemera do contrato
// MCP v2 (spec docs-de-apoio/SPEC-GESTAO-CERTIFICADOS-A1.md):
// - Upload .pfx/.p12 (<=64KB) com senha + consentimento dedicado (§8.2);
// - Card de metadados + badge de status (verde/ambar/vermelho/cinza) (§8.3);
// - Rotacao com confirmacao extra em CNPJ divergente (409 CNPJ_MISMATCH);
// - Remocao com modal de impacto + confirmacao;
// - Aba Historico (AUDIT_ROLES) com acao/usuario/timestamp/IP;
// - Erros amigaveis por codigo (mapa §8.5) — nunca stack/generico.
// Invariante de seguranca: a senha vive apenas no estado do formulario
// durante o submit e e descartada em seguida; nunca em storage do browser.

const CERT_MAX_SIZE_BYTES = 64 * 1024; // 64KB — pre-validacao client-side (§8.2)
const CERT_EXTENSIONS = ['.pfx', '.p12'];
const HISTORY_PAGE_SIZE = 20;

// US-S14-009: gestao (upload/rotacionar/remover) = admin/owner + super_user
// (o backend aceita o bypass SUPER_USER sem impersonation — _require_cert_role).
// Historico segue AUDIT_ROLES do backend (super_user/admin/owner).
const CERT_MANAGE_ROLES = ['admin', 'owner'];

interface UiError {
  key: string;
  params?: Record<string, string | number>;
  detail?: string;
}

function cooldownMinutes(retryAfterSec?: number): number {
  if (!retryAfterSec || retryAfterSec <= 0) return 1;
  return Math.max(1, Math.ceil(retryAfterSec / 60));
}

// Mapa de erros §8.5 — traduz o contrato {detail:{code,message}} em chaves
// i18n. Mensagens do backend sao usadas apenas como detalhe secundario (o
// contrato garante que nunca contem senha/PFX/stack).
function mapCertError(err: unknown): UiError {
  if (err instanceof CertificateApiError) {
    // Codigos reais do backend (app/routers/certificates.py).
    switch (err.code) {
      case 'INVALID_PFX':
        return { key: 'sefaz.certs.error.invalidPfx' };
      case 'INVALID_CERT_CHAIN':
        return { key: 'sefaz.certs.error.invalidChain' };
      case 'CERT_EXPIRED':
        return { key: 'sefaz.certs.error.expired' };
      case 'CNPJ_MISMATCH':
        return { key: 'sefaz.certs.error.cnpjMismatch' };
      case 'RATE_LIMITED':
      case 'CERT_RATE_LIMITED':
      case 'SESSION_RATE_LIMITED':
        return {
          key: 'sefaz.certs.error.rateLimited',
          params: { min: cooldownMinutes(err.retryAfterSec) },
        };
      case 'UPLOAD_DISABLED':
      case 'CERT_UPLOAD_DISABLED':
        return { key: 'sefaz.certs.error.uploadDisabled' };
      case 'NO_ORG_CNPJ':
        return { key: 'sefaz.certs.error.noOrgCnpj' };
      case 'PFX_TOO_LARGE':
        return { key: 'sefaz.certs.error.pfxTooLarge' };
      case 'CERT_ALREADY_EXISTS':
        return { key: 'sefaz.certs.error.alreadyExists' };
      case 'CONSENT_REQUIRED':
        return { key: 'sefaz.certs.error.consentRequired' };
      case 'CONFIRM_REQUIRED':
        return { key: 'sefaz.certs.error.confirmRequired' };
      case 'MCP_UNAVAILABLE':
      case 'TRANSPORT':
      case 'MCP_ERROR':
      case 'SESSION_ERROR':
      case 'SESSION_REQUIRED':
      case 'SESSION_EXPIRED':
        return { key: 'sefaz.certs.error.unavailable' };
      case 'VALIDATION_ERROR':
      case 'CERT_INVALID':
        return { key: 'sefaz.certs.error.validation', detail: err.message };
      default:
        break;
    }
    if (err.status === 429) {
      return {
        key: 'sefaz.certs.error.rateLimited',
        params: { min: cooldownMinutes(err.retryAfterSec) },
      };
    }
    if (err.status === 403) return { key: 'sefaz.certs.error.forbidden' };
    if (err.status >= 500) return { key: 'sefaz.certs.error.unavailable' };
    return { key: 'sefaz.certs.error.generic', detail: err.message };
  }
  // Falha de rede (fetch rejeitado) — servico temporariamente indisponivel.
  return { key: 'sefaz.certs.error.unavailable' };
}

function renderError(err: UiError, t: TFunction): string {
  return t(err.key, err.params);
}

function formatCnpj(cnpj: string | null | undefined): string {
  if (!cnpj) return '—';
  const d = cnpj.replace(/\D/g, '');
  if (d.length !== 14) return cnpj;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

function isExpired(cert: CertificateMetadata): boolean {
  return cert.status === 'expirado' || (cert.days_until_expiry != null && cert.days_until_expiry < 0);
}

function isExpiringSoon(cert: CertificateMetadata): boolean {
  return (
    cert.status === 'vencendo' ||
    (cert.days_until_expiry != null && cert.days_until_expiry >= 0 && cert.days_until_expiry <= 30)
  );
}

interface BadgeInfo {
  key: string;
  params?: Record<string, string | number>;
  cls: string;
}

// Badge de status §8.3: verde ativo / ambar vence em N dias / vermelho
// expirado / cinza pendente_consentimento/removido.
function badgeFor(cert: CertificateMetadata): BadgeInfo {
  if (isExpired(cert)) {
    return { key: 'sefaz.certs.status.expirado', cls: 'bg-red-100 text-red-700' };
  }
  if (cert.status === 'removido' || cert.status === 'pendente_consentimento') {
    return {
      key:
        cert.status === 'pendente_consentimento'
          ? 'sefaz.certs.status.pendente'
          : 'sefaz.certs.status.removido',
      cls: 'bg-gray-200 text-gray-600',
    };
  }
  if (isExpiringSoon(cert)) {
    return {
      key: 'sefaz.certs.status.vencendo',
      params: { n: Math.max(0, cert.days_until_expiry ?? 0) },
      cls: 'bg-amber-100 text-amber-700',
    };
  }
  return { key: 'sefaz.certs.status.ativo', cls: 'bg-green-100 text-green-700' };
}

function SefazCerts({ onNavigate }: { onNavigate?: (view: NavView) => void }) {
  const t = useT();
  const { locale } = useLocale();

  const [me, setMe] = useState<UserMe | null>(null);
  const [loading, setLoading] = useState(true);
  const [cert, setCert] = useState<CertificateMetadata | null>(null);
  // member/sem permissao de metadados: GET /certificates -> 403 -> cai na
  // visao funcional via GET /certificates/status (member-safe).
  const [memberView, setMemberView] = useState(false);
  const [memberStatus, setMemberStatus] = useState<CertificateStatusResponse | null>(null);
  const [loadError, setLoadError] = useState<UiError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [view, setView] = useState<'detail' | 'upload'>('detail');
  const [uploadMode, setUploadMode] = useState<'create' | 'rotate'>('create');
  const [showRemove, setShowRemove] = useState(false);
  const [tab, setTab] = useState<'detail' | 'history'>('detail');
  const [historyKey, setHistoryKey] = useState(0);

  const role = me?.role ?? 'member';
  // super_user sempre gerencia — o backend (_require_cert_role) ja aceita o
  // bypass SUPER_USER sem exigir impersonation; esconder o controle aqui
  // era mais restritivo que a API (feedback do owner, #482 pos-merge).
  const canManage = CERT_MANAGE_ROLES.includes(role) || role === 'super_user';

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      try {
        setMe(await fetchMe());
      } catch {
        // 401 e interceptado pelo cliente HTTP (redirect p/ login); outros
        // erros mantem role='member' — nenhuma acao privilegiada e exibida.
      }
      try {
        // Fix auditoria S14 (HIGH-1): toda chamada de cert envia
        // X-Organization-ID da org ativa (claim impersonating_org_id do
        // JWT) — o backend agora valida a role NA org resolvida.
        const data = await getCertificate(getActiveOrgId());
        setCert(data);
        setMemberView(false);
      } catch (err) {
        if (err instanceof CertificateApiError && err.status === 404) {
          setCert(null);
        } else if (err instanceof CertificateApiError && err.status === 403) {
          setMemberView(true);
          setCert(null);
          try {
            setMemberStatus(await getCertificateStatus(getActiveOrgId()));
          } catch {
            setMemberStatus(null);
          }
        } else {
          setLoadError(mapCertError(err));
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openUpload = useCallback((mode: 'create' | 'rotate') => {
    setUploadMode(mode);
    setView('upload');
    setNotice(null);
  }, []);

  const handleUploadDone = useCallback(
    (created: CertificateMetadata) => {
      setCert(created);
      setMemberView(false);
      setView('detail');
      setHistoryKey((k) => k + 1);
      setNotice(
        t(uploadMode === 'rotate' ? 'sefaz.certs.upload.successRotate' : 'sefaz.certs.upload.successCreate'),
      );
    },
    [t, uploadMode],
  );

  const handleRemoved = useCallback(() => {
    setCert(null);
    setShowRemove(false);
    setHistoryKey((k) => k + 1);
    setNotice(t('sefaz.certs.remove.success'));
  }, [t]);

  if (loading) {
    return (
      <Page title={t('sefaz.certs.title')}>
        <p className="text-sm text-brand-600">{t('common.loading')}</p>
      </Page>
    );
  }

  return (
    <Page title={t('sefaz.certs.title')} subtitle={t('sefaz.certs.subtitle')}>
      {/* Erro fatal de carga (rede/backend) com retry manual. */}
      {loadError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <p>{renderError(loadError, t)}</p>
          {loadError.detail && <p className="mt-1 text-xs text-red-500">{loadError.detail}</p>}
          <button
            type="button"
            onClick={() => void load()}
            className="mt-3 rounded-md border border-red-300 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
          >
            {t('sefaz.certs.retry')}
          </button>
        </div>
      )}

      {/* Visao funcional (member-safe): apenas status, sem metadados/acoes. */}
      {!loadError && memberView && (
        <div className="rounded-lg border border-brand-200 bg-brand-50 p-6">
          <p className="mb-2 font-semibold text-brand-800">{t('sefaz.certs.member.title')}</p>
          <p className="text-sm text-brand-700">
            {memberStatus && memberStatus.has_certificate === false
              ? t('sefaz.certs.member.absent')
              : memberStatus?.status === 'ativo'
                ? t('sefaz.certs.member.configured')
                : memberStatus?.status === 'vencendo'
                  ? t('sefaz.certs.member.expiring', { n: memberStatus.days_until_expiry ?? '?' })
                  : memberStatus?.status === 'expirado'
                    ? t('sefaz.certs.member.expired')
                    : t('sefaz.certs.member.absent')}
          </p>
          <p className="mt-2 text-xs text-brand-500">{t('sefaz.certs.member.hint')}</p>
        </div>
      )}

      {!loadError && !memberView && view === 'upload' && (
        <CertificateUploadForm
          mode={uploadMode}
          cnpj={cert?.cnpj ?? null}
          onDone={handleUploadDone}
          onCancel={() => setView('detail')}
          onNavigate={onNavigate}
        />
      )}

      {!loadError && !memberView && view === 'detail' && (
        <>
          {notice && (
            <div className="mb-4 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">
              {notice}
            </div>
          )}

          {/* US-S14-003/007: banner vermelho persistente para cert expirado. */}
          {cert && isExpired(cert) && (
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-red-300 bg-red-50 p-4">
              <p className="text-sm font-medium text-red-800">{t('sefaz.certs.expiredBanner')}</p>
              {canManage && (
                <button
                  type="button"
                  onClick={() => openUpload('rotate')}
                  className="rounded-md bg-red-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-red-700"
                >
                  {t('sefaz.certs.expiredCta')}
                </button>
              )}
            </div>
          )}

          {canManage && (
            <div className="mb-4 flex gap-2 border-b border-brand-200">
              <button
                className={`px-4 py-2 font-medium ${tab === 'detail' ? 'border-b-2 border-brand-600 text-brand-700' : 'text-brand-400'}`}
                onClick={() => setTab('detail')}
              >
                {t('sefaz.certs.tab.detail')}
              </button>
              <button
                className={`px-4 py-2 font-medium ${tab === 'history' ? 'border-b-2 border-brand-600 text-brand-700' : 'text-brand-400'}`}
                onClick={() => setTab('history')}
              >
                {t('sefaz.certs.tab.history')}
              </button>
            </div>
          )}

          {tab === 'history' && canManage ? (
            <CertificateHistory refreshKey={historyKey} />
          ) : !cert ? (
            /* US-S14-007: empty state NO_CERTIFICATE — CTA por papel. */
            <div className="rounded-lg border border-brand-200 bg-brand-50 p-6 text-center">
              <p className="mb-2 font-semibold text-brand-800">{t('sefaz.certs.empty.title')}</p>
              <p className="mb-4 text-sm text-brand-600">
                {canManage ? t('sefaz.certs.empty.body') : t('sefaz.certs.empty.memberHint')}
              </p>
              {canManage && (
                <button
                  type="button"
                  onClick={() => openUpload('create')}
                  className="rounded-lg bg-brand-600 px-6 py-2 font-medium text-white hover:bg-brand-700"
                >
                  {t('sefaz.certs.empty.cta')}
                </button>
              )}
            </div>
          ) : (
            <div className="rounded-lg border border-brand-200 bg-white p-6 shadow-sm">
              <div className="mb-4 flex items-center justify-between gap-3">
                <h2 className="text-lg font-semibold text-brand-900">{t('sefaz.certs.meta.title')}</h2>
                {(() => {
                  const b = badgeFor(cert);
                  return (
                    <span className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${b.cls}`}>
                      {t(b.key, b.params)}
                    </span>
                  );
                })()}
              </div>

              <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
                <MetaField label={t('sefaz.certs.meta.cn')} value={cert.cn} />
                <MetaField label={t('sefaz.certs.meta.issuer')} value={cert.issuer} />
                <MetaField label={t('sefaz.certs.meta.serial')} value={cert.serial} mono />
                <MetaField label={t('sefaz.certs.meta.cnpj')} value={formatCnpj(cert.cnpj)} mono />
                <MetaField label={t('sefaz.certs.meta.validFrom')} value={formatDate(cert.valid_from, locale)} />
                <MetaField label={t('sefaz.certs.meta.validTo')} value={formatDate(cert.valid_to, locale)} />
                <MetaField
                  label={t('sefaz.certs.meta.fingerprint')}
                  value={cert.fingerprint_sha256}
                  mono
                />
                <MetaField label={t('sefaz.certs.meta.uploadedAt')} value={formatDateTime(cert.created_at, locale)} />
                <MetaField label={t('sefaz.certs.meta.uploadedBy')} value={cert.uploaded_by_email} />
                <MetaField
                  label={t('sefaz.certs.meta.lastSession')}
                  value={formatDateTime(cert.last_session_at, locale)}
                />
              </dl>

              {canManage && (
                <div className="mt-6 flex gap-3 border-t border-brand-100 pt-4">
                  <button
                    type="button"
                    onClick={() => openUpload('rotate')}
                    className="rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800"
                  >
                    {t('sefaz.certs.actions.rotate')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowRemove(true)}
                    className="rounded-md border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
                  >
                    {t('sefaz.certs.actions.remove')}
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* US-S14-005: modal de remocao com aviso de impacto + confirmacao. */}
      {showRemove && cert && (
        <RemoveCertificateModal onClose={() => setShowRemove(false)} onRemoved={handleRemoved} />
      )}
    </Page>
  );
}

function MetaField({ label, value, mono }: { label: string; value?: string | null; mono?: boolean }) {
  return (
    <div>
      <dt className="text-xs font-medium text-brand-500">{label}</dt>
      <dd className={`mt-0.5 text-brand-800 ${mono ? 'break-all font-mono text-xs' : ''}`}>
        {value || '—'}
      </dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Formulario de upload/rotacao (§8.2)
// ---------------------------------------------------------------------------

function CertificateUploadForm({
  mode,
  cnpj,
  onDone,
  onCancel,
  onNavigate,
}: {
  mode: 'create' | 'rotate';
  cnpj: string | null;
  onDone: (cert: CertificateMetadata) => void;
  onCancel: () => void;
  onNavigate?: (view: NavView) => void;
}) {
  const t = useT();
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  // A senha vive somente neste estado ate o submit — nunca em storage do
  // browser. E limpa apos sucesso/erro terminal; no fluxo 409 CNPJ_MISMATCH
  // e retida apenas enquanto o dialogo de confirmacao estiver aberto.
  const [senha, setSenha] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const [dragging, setDragging] = useState(false);
  const [mismatchPending, setMismatchPending] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback(
    (f: File): string | null => {
      const name = f.name.toLowerCase();
      if (!CERT_EXTENSIONS.some((ext) => name.endsWith(ext))) {
        return t('sefaz.certs.upload.fileErrorExt');
      }
      if (f.size > CERT_MAX_SIZE_BYTES) {
        return t('sefaz.certs.upload.fileErrorSize');
      }
      return null;
    },
    [t],
  );

  const pickFile = useCallback(
    (f: File | null | undefined) => {
      if (!f) return;
      const err = validateFile(f);
      if (err) {
        setFileError(err);
        setFile(null);
        return;
      }
      setFileError(null);
      setFile(f);
    },
    [validateFile],
  );

  const submit = useCallback(
    async (confirmMismatch: boolean) => {
      if (!file || !senha || !consent || busy) return;
      setBusy(true);
      setError(null);
      try {
        const result =
          mode === 'rotate'
            ? await rotateCertificate(file, senha, consent, confirmMismatch, getActiveOrgId())
            : await uploadCertificate(file, senha, consent, getActiveOrgId());
        onDone(result);
      } catch (err) {
        // US-S14-004: CNPJ divergente na rotacao -> confirmacao explicita
        // adicional; reenvia com confirm_cnpj_mismatch=true.
        if (
          mode === 'rotate' &&
          !confirmMismatch &&
          err instanceof CertificateApiError &&
          err.code === 'CNPJ_MISMATCH'
        ) {
          setMismatchPending(true);
          setBusy(false);
          return;
        }
        setError(mapCertError(err));
        setSenha('');
      } finally {
        setBusy(false);
      }
    },
    [file, senha, consent, busy, mode, onDone],
  );

  const consentText = cnpj
    ? t('sefaz.certs.consent.text', { cnpj: formatCnpj(cnpj) })
    : t('sefaz.certs.consent.textNoCnpj');

  const canSubmit = !!file && senha.length > 0 && consent && !busy;

  return (
    <div className="rounded-lg border border-brand-200 bg-white p-6 shadow-sm">
      <h2 className="mb-4 text-lg font-semibold text-brand-900">
        {t(mode === 'rotate' ? 'sefaz.certs.upload.titleRotate' : 'sefaz.certs.upload.titleCreate')}
      </h2>

      {/* Dropzone .pfx/.p12 com validacao client-side de extensao/tamanho. */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          pickFile(e.dataTransfer.files[0]);
        }}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
          dragging ? 'border-brand-500 bg-brand-50' : 'border-brand-300 hover:border-brand-400 hover:bg-brand-50'
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={CERT_EXTENSIONS.join(',')}
          className="hidden"
          onChange={(e) => {
            pickFile(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
        <div className="text-brand-500">
          <svg
            className="mx-auto mb-3 h-12 w-12"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
            />
          </svg>
          <p className="text-sm font-medium text-brand-700">
            {file ? file.name : t('sefaz.certs.upload.dropzone')}
          </p>
          <p className="mt-1 text-xs text-brand-400">
            {file
              ? `${(file.size / 1024).toFixed(1)} KB — ${t('sefaz.certs.upload.changeFile')}`
              : t('sefaz.certs.upload.formats')}
          </p>
        </div>
      </div>
      {fileError && <p className="mt-2 text-sm text-red-600">{fileError}</p>}

      {/* Senha do certificado — type=password, sem autocomplete, nunca
          persistida no navegador (limpa apos o submit). */}
      <div className="mt-4">
        <label className="mb-1 block text-sm font-medium text-brand-600">
          {t('sefaz.certs.upload.passwordLabel')}
        </label>
        <input
          type="password"
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
          // new-password: impede autofill de credenciais salvas — a senha do
          // PFX nunca deve ser persistida pelo gerenciador do navegador.
          autoComplete="new-password"
          className="w-full rounded-lg border border-brand-200 px-4 py-2 text-sm"
        />
        <p className="mt-1 text-xs text-brand-400">{t('sefaz.certs.upload.passwordHint')}</p>
      </div>

      {/* Consentimento dedicado (cert_a1_upload_v2) — 2o aceite autonomo,
          obrigatorio para habilitar o submit, com link para o termo de
          custodia (politica de privacidade — pagina dedicada pendente). */}
      <label className="mt-4 flex items-start gap-3 rounded-lg border border-brand-200 bg-brand-50 p-4">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-1 h-4 w-4 shrink-0 accent-brand-700"
        />
        <span className="text-xs leading-relaxed text-brand-700">
          {consentText}{' '}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onNavigate?.('custodia');
            }}
            className="font-medium text-brand-700 underline hover:text-brand-900"
          >
            {t('sefaz.certs.consent.linkLabel')}
          </a>
        </span>
      </label>

      {error && (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <p>{renderError(error, t)}</p>
          {error.detail && <p className="mt-1 text-xs text-red-500">{error.detail}</p>}
        </div>
      )}

      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="rounded-md border border-brand-300 px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50"
        >
          {t('common.cancel')}
        </button>
        <button
          type="button"
          onClick={() => void submit(false)}
          disabled={!canSubmit}
          className="rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy
            ? t('common.processing')
            : t(mode === 'rotate' ? 'sefaz.certs.upload.submitRotate' : 'sefaz.certs.upload.submitCreate')}
        </button>
      </div>

      {/* US-S14-004: dialogo de confirmacao de CNPJ divergente (409). */}
      {mismatchPending && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="mb-2 text-lg font-semibold text-brand-900">
              {t('sefaz.certs.mismatch.title')}
            </h3>
            <p className="mb-4 text-sm text-brand-600">{t('sefaz.certs.mismatch.body')}</p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setMismatchPending(false);
                  setSenha('');
                }}
                className="rounded-md border border-brand-300 px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setMismatchPending(false);
                  void submit(true);
                }}
                className="rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800"
              >
                {t('sefaz.certs.mismatch.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modal de remocao (US-S14-005 / §8.5)
// ---------------------------------------------------------------------------

function RemoveCertificateModal({
  onClose,
  onRemoved,
}: {
  onClose: () => void;
  onRemoved: () => void;
}) {
  const t = useT();
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);

  const confirm = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await deleteCertificate(getActiveOrgId());
      onRemoved();
    } catch (err) {
      setError(mapCertError(err));
      setBusy(false);
    }
  }, [onRemoved]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h2 className="mb-2 text-lg font-semibold text-brand-900">{t('sefaz.certs.remove.title')}</h2>
        <p className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
          {t('sefaz.certs.remove.impact')}
        </p>
        <label className="mb-4 flex items-start gap-3">
          <input
            type="checkbox"
            checked={ack}
            onChange={(e) => setAck(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 accent-red-600"
          />
          <span className="text-sm text-brand-700">{t('sefaz.certs.remove.confirmLabel')}</span>
        </label>
        {error && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <p>{renderError(error, t)}</p>
            {error.detail && <p className="mt-1 text-xs text-red-500">{error.detail}</p>}
          </div>
        )}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-brand-300 px-4 py-2 text-sm font-medium text-brand-700 hover:bg-brand-50 disabled:opacity-50"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={() => void confirm()}
            disabled={!ack || busy}
            className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? t('common.processing') : t('sefaz.certs.remove.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Aba Historico (US-S14-006) — restrita a AUDIT_ROLES (renderizada apenas
// quando canManage; o backend responde 403 para member/contador).
// ---------------------------------------------------------------------------

function historyIp(details: Record<string, unknown> | undefined): string {
  if (!details) return '—';
  const ip = details.ip ?? details.ip_address ?? details.ipAddress;
  return typeof ip === 'string' && ip ? ip : '—';
}

function CertificateHistory({ refreshKey }: { refreshKey: number }) {
  const t = useT();
  const { locale } = useLocale();
  const [items, setItems] = useState<CertificateHistoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<UiError | null>(null);

  const load = useCallback(async (p: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getCertificateHistory(p, HISTORY_PAGE_SIZE, getActiveOrgId());
      setItems(res.items);
      setTotal(res.total);
      setPage(p);
    } catch (err) {
      setError(mapCertError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(1);
  }, [load, refreshKey]);

  const pages = Math.max(1, Math.ceil(total / HISTORY_PAGE_SIZE));

  if (loading) {
    return <p className="text-sm text-brand-600">{t('common.loading')}</p>;
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        <p>{renderError(error, t)}</p>
        {error.detail && <p className="mt-1 text-xs text-red-500">{error.detail}</p>}
        <button
          type="button"
          onClick={() => void load(page)}
          className="mt-3 rounded-md border border-red-300 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
        >
          {t('sefaz.certs.retry')}
        </button>
      </div>
    );
  }

  if (items.length === 0) {
    return <p className="text-sm text-brand-500">{t('sefaz.certs.history.empty')}</p>;
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-brand-200 text-brand-700">
            <tr>
              <th className="py-2 pr-4">{t('sefaz.certs.history.col.action')}</th>
              <th className="py-2 pr-4">{t('sefaz.certs.history.col.user')}</th>
              <th className="py-2 pr-4">{t('sefaz.certs.history.col.timestamp')}</th>
              <th className="py-2 pr-4">{t('sefaz.certs.history.col.ip')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => {
              const actionKey = `sefaz.certs.history.action.${item.action}`;
              const translated = t(actionKey);
              return (
                <tr key={`${item.timestamp ?? idx}-${idx}`} className="border-b border-brand-100">
                  <td className="py-2 pr-4 text-brand-800">
                    {translated !== actionKey ? translated : item.action}
                  </td>
                  <td className="py-2 pr-4 text-brand-600">{item.user_email ?? '—'}</td>
                  <td className="py-2 pr-4 text-xs text-brand-500">
                    {formatDateTime(item.timestamp, locale)}
                  </td>
                  <td className="py-2 pr-4 font-mono text-xs text-brand-500">
                    {historyIp(item.details)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <button
            type="button"
            onClick={() => void load(page - 1)}
            disabled={page <= 1}
            className="rounded-md border border-brand-300 px-3 py-1.5 font-medium text-brand-700 hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('sefaz.certs.history.prev')}
          </button>
          <span className="text-brand-500">{t('sefaz.certs.history.page', { page, pages })}</span>
          <button
            type="button"
            onClick={() => void load(page + 1)}
            disabled={page >= pages}
            className="rounded-md border border-brand-300 px-3 py-1.5 font-medium text-brand-700 hover:bg-brand-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t('sefaz.certs.history.next')}
          </button>
        </div>
      )}
    </div>
  );
}

export default SefazCerts;
